import { useState, useRef, useCallback, useEffect } from 'react';
import { Bot, User, Loader2, FileSpreadsheet, TrendingUp, BarChart3, DollarSign, Copy, Check, Paperclip, Image, BookOpen, X } from 'lucide-react';
import ToolCallCard from '../shared/ToolCallCard';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MarkdownTable } from '../data-explorer/MarkdownTable';
import { HtmlBlock } from '../shared/HtmlBlock';
import { normalizeMarkdown } from '../../utils/markdownUtils';
import { chatTheme as th } from '../../theme/chat';
import { modelBuilderApi } from '../../services/modelBuilderApi';
import XlsxPreview from './XlsxPreview';

const MODELS = [
  { id: 'google_genai:gemini-3.5-flash', name: 'Gemini 3.5 Flash' },
  { id: 'google_genai:gemini-3.1-pro-preview', name: 'Gemini 3.1 Pro' },
  { id: 'google_genai:gemini-2.5-pro', name: 'Gemini 2.5 Pro' },
  { id: 'google_genai:gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
  { id: 'anthropic:claude-opus-4-8', name: 'Claude Opus 4.8' },
];

const QUICK_ACTIONS = [
  { icon: TrendingUp, label: 'P&L Projection', prompt: 'Build a monthly P&L projection for the next 12 months with 10% revenue growth' },
  { icon: FileSpreadsheet, label: '3-Statement Model', prompt: 'Create a 3-statement financial model (P&L, Balance Sheet, Cash Flow) for the last 12 months with projections' },
  { icon: BarChart3, label: 'Budget vs Actual', prompt: 'Build a budget vs actual variance analysis for the current year' },
  { icon: DollarSign, label: 'Revenue by Customer', prompt: 'Create a revenue breakdown by top 10 customers with trend analysis' },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };
  return (
    <button onClick={handleCopy} className={`p-1 rounded transition-colors ${th.copyBtn.base}`} title="Copy">
      {copied ? <Check className={`h-3.5 w-3.5 ${th.copyBtn.success}`} /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

export function ModelBuilderChat({ threadId: externalThreadId, selectedCompanies, onSelectCompany, onRemoveCompany, onThreadCreated }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(externalThreadId);
  const [input, setInput] = useState('');
  const [images, setImages] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedModel, setSelectedModel] = useState(MODELS[0].id);
  const [skills, setSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [showSkillPicker, setShowSkillPicker] = useState(false);
  const messagesEndRef = useRef(null);
  const abortRef = useRef(null);
  const sendingRef = useRef(false);
  const justStreamedThreadRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    setThreadId(externalThreadId);
    if (sendingRef.current) return;
    if (externalThreadId && externalThreadId === justStreamedThreadRef.current) {
      justStreamedThreadRef.current = null;
      return;
    }
    if (externalThreadId) {
      modelBuilderApi.getThread(externalThreadId).then(data => {
        if (sendingRef.current) return;
        setMessages((data.messages || []).map(m => {
          const segs = [];
          if (m.toolCalls?.length) m.toolCalls.forEach(tc => segs.push({ type: 'tool_call', tool: tc.tool, status: tc.status || 'done' }));
          if (m.content) segs.push({ type: 'text', content: m.content });
          return { role: m.role, content: m.content || '', toolCalls: m.toolCalls || [], segments: segs };
        }));
      }).catch(() => { if (!sendingRef.current) setMessages([]); });
    } else {
      setMessages([]);
    }
  }, [externalThreadId]);

  useEffect(() => {
    modelBuilderApi.getSkills().then(data => {
      setSkills(data.data || []);
    }).catch(() => {});
  }, []);

  const toggleSkill = useCallback((skill) => {
    setSelectedSkills(prev => {
      const exists = prev.find(s => s.name === skill.name);
      if (exists) return prev.filter(s => s.name !== skill.name);
      return [...prev, skill];
    });
  }, []);

  const updateLast = (updater) => {
    setMessages(prev => {
      const last = prev[prev.length - 1];
      if (last?.role === 'assistant') return [...prev.slice(0, -1), updater(last)];
      return prev;
    });
  };

  const handleFileUpload = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const AGENT_BASE = window._env_?.REACT_APP_TEMPLATE_AGENT_URL || 'http://localhost:8001';
      const resp = await fetch(`${AGENT_BASE}/models/upload`, { method: 'POST', body: formData });
      const data = await resp.json();
      setUploadedFiles(prev => [...prev, data]);
      return data;
    } catch (err) {
      console.error('Upload failed:', err);
      return null;
    }
  }, []);

  const handleImagePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          setImages(prev => [...prev, { base64, mime_type: item.type }]);
        };
        reader.readAsDataURL(blob);
      }
    }
  }, []);

  const handleSend = useCallback(async (text) => {
    if ((!text?.trim() && !images.length && !uploadedFiles.length) || sendingRef.current) return;
    sendingRef.current = true;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Build message with file and skill context
    let fullMessage = text || '';
    if (selectedSkills.length > 0) {
      const skillContext = selectedSkills.map(s => `<skill name="${s.name}">\n${s.content}\n</skill>`).join('\n\n');
      fullMessage = `Use the following skills as reference:\n\n${skillContext}\n\n${fullMessage}`;
    }
    if (uploadedFiles.length > 0) {
      const fileInfo = uploadedFiles.map(f => `[Uploaded file: ${f.filename} at ${f.path}]`).join('\n');
      fullMessage = `${fileInfo}\n\n${fullMessage}`;
    }

    setLoading(true);
    setInput('');
    const sentImages = [...images];
    setImages([]);
    const sentFiles = [...uploadedFiles];
    setUploadedFiles([]);
    setMessages(prev => [...prev,
      { role: 'user', content: text || '(files attached)', images: sentImages, files: sentFiles, segments: [] },
      { role: 'assistant', content: '', toolCalls: [], segments: [] },
    ]);

    const body = {
      message: fullMessage,
      user_id: 'admin',
      thread_id: threadId,
      model: selectedModel || undefined,
      images: sentImages.length > 0 ? sentImages.map(img => ({ base64: img.base64, mime_type: img.mime_type })) : undefined,
      context: selectedCompanies?.length > 0 ? {
        selected_rf_ids: selectedCompanies.map(c => c.rf_id),
        selected_companies: selectedCompanies.map(c => ({ rf_id: c.rf_id, name: c.company_name, source_type: c.source_type })),
      } : undefined,
    };

    try {
      const response = await modelBuilderApi.streamChat(body, controller.signal);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:') && currentEvent) {
            const eventType = currentEvent;
            currentEvent = null;
            try {
              const data = JSON.parse(line.slice(5).trim());
              switch (eventType) {
                case 'thread_id':
                  setThreadId(data.thread_id);
                  abortRef.current._newThreadId = data.thread_id;
                  break;
                case 'token':
                  updateLast(last => {
                    const segs = [...(last.segments || [])];
                    const lastSeg = segs[segs.length - 1];
                    if (lastSeg?.type === 'text') {
                      segs[segs.length - 1] = { ...lastSeg, content: lastSeg.content + data.content };
                    } else {
                      segs.push({ type: 'text', content: data.content });
                    }
                    return { ...last, content: last.content + data.content, segments: segs };
                  });
                  break;
                case 'tool_call':
                  updateLast(last => {
                    const existing = last.toolCalls || [];
                    if (existing.some(t => t.tool === data.tool && t.status === 'running')) return last;
                    const segs = [...(last.segments || [])];
                    segs.push({ type: 'tool_call', tool: data.tool, status: 'running' });
                    return { ...last, toolCalls: [...existing, { tool: data.tool, status: 'running' }], segments: segs };
                  });
                  break;
                case 'tool_result':
                  updateLast(last => {
                    const newToolCalls = (last.toolCalls || []).map(tc =>
                      tc.tool === data.tool && tc.status === 'running' ? { ...tc, status: 'done' } : tc
                    );
                    const segs = [...(last.segments || [])];
                    for (let i = segs.length - 1; i >= 0; i--) {
                      if (segs[i].type === 'tool_call' && segs[i].tool === data.tool && segs[i].status === 'running') {
                        segs[i] = { ...segs[i], status: 'done' };
                        break;
                      }
                    }
                    return { ...last, toolCalls: newToolCalls, segments: segs };
                  });
                  break;
                case 'model_file': {
                  // Tool handler emits: {"type":"model_file","data":{file_id,filename,...}}
                  const mf = data.data || data;
                  updateLast(last => {
                    const segs = [...(last.segments || [])];
                    segs.push({
                      type: 'model_file',
                      file_id: mf.file_id,
                      filename: mf.filename,
                      download_url: mf.download_url,
                      preview_url: mf.preview_url,
                    });
                    return { ...last, segments: segs };
                  });
                  break;
                }
                case 'html_block':
                  updateLast(last => {
                    const segs = [...(last.segments || [])];
                    segs.push({ type: 'html_block', html: data.data?.html || data.html, id: data.data?.id || data.id });
                    return { ...last, segments: segs };
                  });
                  break;
                case 'error':
                  updateLast(last => ({ ...last, content: last.content + `\n\nError: ${data.message}` }));
                  break;
                case 'done':
                  break;
                default:
                  break;
              }
            } catch {}
          } else if (line.trim() === '') {
            currentEvent = null;
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') { sendingRef.current = false; return; }
      updateLast(last => ({ ...last, content: `Error: ${err.message}` }));
    }

    sendingRef.current = false;
    setLoading(false);
    const newTid = abortRef.current?._newThreadId;
    if (newTid) {
      justStreamedThreadRef.current = newTid;
      if (onThreadCreated) onThreadCreated(newTid);
    }
  }, [threadId, selectedCompanies, onThreadCreated, images, uploadedFiles, selectedModel, selectedSkills]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  };

  const isEmpty = messages.length === 0;

  const quickActionsBar = (
    <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
      {QUICK_ACTIONS.map((action) => (
        <button
          key={action.label}
          onClick={() => handleSend(action.prompt)}
          disabled={loading}
          className={`flex items-center gap-2 px-4 py-2 text-sm border rounded-full
                     transition-all disabled:opacity-50 whitespace-nowrap flex-shrink-0
                     ${th.quickAction.text} ${th.quickAction.border} ${th.quickAction.hover}`}
        >
          <action.icon className="h-4 w-4" />
          {action.label}
        </button>
      ))}
    </div>
  );

  const inputBar = (
    <div className="max-w-3xl mx-auto px-3 py-2">
      {/* Selected skills preview */}
      {selectedSkills.length > 0 && (
        <div className="flex gap-1.5 mb-2 flex-wrap">
          {selectedSkills.map(s => (
            <span key={s.name} className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-900/30 border border-emerald-700/50 rounded text-xs text-emerald-300">
              <BookOpen className="h-3 w-3" />
              {s.name}
              <button onClick={() => toggleSkill(s)} className="ml-0.5 hover:text-red-400"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      {/* Attached items preview */}
      {(images.length > 0 || uploadedFiles.length > 0) && (
        <div className="flex gap-2 mb-2 flex-wrap">
          {images.map((img, i) => (
            <div key={`img-${i}`} className="relative">
              <img src={`data:${img.mime_type};base64,${img.base64}`} alt="attached" className="max-w-[80px] max-h-[60px] rounded border border-gray-600" />
              <button onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white rounded-full text-xs leading-none">×</button>
            </div>
          ))}
          {uploadedFiles.map((f, i) => (
            <div key={`file-${i}`} className="flex items-center gap-1 px-2 py-1 bg-gray-700 rounded text-xs text-gray-200">
              <FileSpreadsheet className="h-3 w-3 text-emerald-400" />
              {f.filename}
              <button onClick={() => setUploadedFiles(prev => prev.filter((_, j) => j !== i))}
                className="ml-1 text-red-400 hover:text-red-300">×</button>
            </div>
          ))}
        </div>
      )}
      <div className={`flex items-end gap-2 rounded-xl border px-3 py-2 ${th.inputBar?.border || 'border-zinc-700'} ${th.inputBar?.bg || 'bg-zinc-800'}`}>
        {/* File upload button */}
        <input ref={fileInputRef} type="file" accept=".xlsx,.csv,.xls,.pdf" className="hidden"
          onChange={async (e) => { if (e.target.files?.[0]) { await handleFileUpload(e.target.files[0]); e.target.value = ''; } }} />
        <button onClick={() => fileInputRef.current?.click()} disabled={loading}
          className="flex-shrink-0 p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200 disabled:opacity-40" title="Upload file (xlsx, csv)">
          <Paperclip className="h-4 w-4" />
        </button>
        {/* Image upload button */}
        <input ref={imageInputRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              const reader = new FileReader();
              reader.onload = () => setImages(prev => [...prev, { base64: reader.result.split(',')[1], mime_type: file.type }]);
              reader.readAsDataURL(file);
              e.target.value = '';
            }
          }} />
        <button onClick={() => imageInputRef.current?.click()} disabled={loading}
          className="flex-shrink-0 p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200 disabled:opacity-40" title="Upload image">
          <Image className="h-4 w-4" />
        </button>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handleImagePaste}
          placeholder="Ask about financial models, projections, or analysis..."
          rows={1}
          disabled={loading}
          className={`flex-1 resize-none bg-transparent text-sm outline-none min-h-[28px] max-h-40 ${th.inputBar?.text || 'text-zinc-100'} ${th.inputBar?.placeholder || 'placeholder:text-zinc-500'} disabled:opacity-50`}
          style={{ overflowY: 'auto' }}
        />
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setShowSkillPicker(p => !p)}
            className={`p-1.5 rounded transition-colors ${selectedSkills.length > 0 ? 'text-emerald-400 bg-emerald-900/30' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'}`}
            title={selectedSkills.length > 0 ? `${selectedSkills.length} skill(s) loaded` : 'Load skills'}
          >
            <BookOpen className="h-4 w-4" />
          </button>
          {showSkillPicker && (
            <div className="absolute bottom-full mb-2 right-0 w-64 bg-zinc-800 border border-zinc-600 rounded-lg shadow-xl z-50 overflow-hidden">
              <div className="px-3 py-2 border-b border-zinc-700 flex items-center justify-between">
                <span className="text-xs font-medium text-zinc-300">Load Skills</span>
                <button onClick={() => setShowSkillPicker(false)} className="text-zinc-500 hover:text-zinc-300">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto p-1">
                {skills.length === 0 && (
                  <div className="text-xs text-zinc-500 text-center py-3">No skills available</div>
                )}
                {skills.map(s => {
                  const isSelected = selectedSkills.some(ss => ss.name === s.name);
                  return (
                    <button
                      key={s.name}
                      onClick={() => toggleSkill(s)}
                      className={`w-full text-left px-2.5 py-1.5 rounded text-xs transition-colors flex items-center gap-2
                        ${isSelected ? 'bg-emerald-900/40 text-emerald-300' : 'text-zinc-300 hover:bg-zinc-700'}`}
                    >
                      <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center
                        ${isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-zinc-500'}`}>
                        {isSelected && <Check className="h-2.5 w-2.5 text-white" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{s.name}</div>
                        {s.description && <div className="text-zinc-500 truncate text-[10px]">{s.description}</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <select
          value={selectedModel}
          onChange={e => setSelectedModel(e.target.value)}
          className="flex-shrink-0 text-xs bg-zinc-700 text-zinc-200 border border-zinc-600 rounded-lg px-2 py-1.5 outline-none cursor-pointer hover:bg-zinc-600 transition-colors"
          title="Select AI model"
        >
          {MODELS.map(m => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <button
          onClick={() => handleSend(input)}
          disabled={loading || (!input.trim() && !images.length && !uploadedFiles.length)}
          className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40
                     ${th.sendBtn?.bg || 'bg-indigo-600'} ${th.sendBtn?.hover || 'hover:bg-indigo-500'} ${th.sendBtn?.text || 'text-white'}`}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
        </button>
      </div>
    </div>
  );

  if (isEmpty) {
    return (
      <div className={`flex-1 flex flex-col ${th.pageBg} overflow-hidden`} style={{ overscrollBehavior: 'none' }}>
        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <div className="mb-8 text-center">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-emerald-900/40 border border-emerald-700/50`}>
              <FileSpreadsheet className="h-7 w-7 text-emerald-400" />
            </div>
            <h1 className={`text-3xl font-light tracking-tight ${th.greeting?.text || 'text-zinc-100'}`}>
              Model Builder
            </h1>
            <p className={`mt-2 text-sm ${th.greeting?.subtext || 'text-zinc-400'}`}>
              Build financial models, projections, and analysis from your data
            </p>
          </div>
          <div className="w-full max-w-3xl mx-auto">
            {inputBar}
            <div className="px-3 mt-3">
              {quickActionsBar}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col ${th.pageBg} overflow-hidden`} style={{ overscrollBehavior: 'none' }}>
      <div className="flex-1 basis-0 overflow-y-auto overflow-x-hidden px-4 py-4" style={{ overscrollBehavior: 'none' }}>
        <div className="max-w-3xl mx-auto space-y-6 w-full">
          {messages.map((msg, i) => (
            <div key={i} className={`group relative flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
              {msg.role === 'assistant' && (
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${th.botIcon}`}>
                  <Bot className="h-4 w-4" />
                </div>
              )}
              <div className={`${msg.role === 'user'
                ? `max-w-[85%] ${th.userBubble}`
                : `flex-1 min-w-0 ${th.assistantText}`}`}>
                {msg.role === 'assistant' && (msg.segments || []).map((seg, j) => {
                  if (seg.type === 'tool_call') {
                    return <ToolCallCard key={j} tool={seg.tool} status={seg.status} result={seg.result} />;
                  }
                  if (seg.type === 'model_file') {
                    return (
                      <div key={j} className="my-3">
                        <XlsxPreview fileId={seg.file_id} filename={seg.filename} />
                      </div>
                    );
                  }
                  if (seg.type === 'html_block' && seg.html) {
                    return (
                      <div key={j} className="my-3">
                        <HtmlBlock html={seg.html} id={seg.id} onFormSubmit={(text) => handleSend(text)} />
                      </div>
                    );
                  }
                  if (seg.type === 'text' && seg.content) {
                    return (
                      <div key={j} className={`my-3 ${th.prose}`}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ table: MarkdownTable }}>
                          {normalizeMarkdown(seg.content)}
                        </ReactMarkdown>
                      </div>
                    );
                  }
                  return null;
                })}
                {msg.role === 'user' && (
                  <div>
                    {msg.images?.length > 0 && (
                      <div className="flex gap-2 mb-2 flex-wrap">
                        {msg.images.map((img, idx) => (
                          <img key={idx} src={`data:${img.mime_type};base64,${img.base64}`}
                            alt="attached" className="max-w-[120px] max-h-[80px] rounded border border-gray-600" />
                        ))}
                      </div>
                    )}
                    {msg.files?.length > 0 && (
                      <div className="flex gap-2 mb-2 flex-wrap">
                        {msg.files.map((f, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-600 rounded text-xs">
                            <FileSpreadsheet className="h-3 w-3 text-emerald-400" />{f.filename}
                          </span>
                        ))}
                      </div>
                    )}
                    {msg.content}
                  </div>
                )}
                {msg.role === 'assistant' && loading && i === messages.length - 1 && !(msg.segments?.length) && (
                  <div className="flex items-center gap-2 h-7">
                    <Loader2 className={`h-4 w-4 animate-spin ${th.spinner}`} />
                    <span className={`text-sm ${th.thinkingText}`}>Building model...</span>
                  </div>
                )}
              </div>
              {msg.content && (
                <div className={`opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 self-end mb-1 ${msg.role === 'user' ? 'order-first' : ''}`}>
                  <CopyButton text={msg.content} />
                </div>
              )}
              {msg.role === 'user' && (
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${th.userIcon}`}>
                  <User className="h-4 w-4" />
                </div>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
      <div className={`${th.bottomBar?.border || 'border-t border-zinc-700'} ${th.bottomBar?.bg || 'bg-zinc-900'}`}>
        {inputBar}
        <div className="max-w-3xl mx-auto px-3 pb-2">
          {quickActionsBar}
        </div>
      </div>
    </div>
  );
}
